import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react"

import ErrorBoundary from "../components/ErrorBoundary"
import { InvoiceViewer, DocumentViewer, AgreementViewer } from "../components/fileViewer"
import { GenericFileViewer } from "../components/fileViewer/GenericFileViewer"
import {
  DownloadLink,
  Invoice,
  Maybe,
  Document,
  CreditNote,
  GeneratedDocument,
} from "@oysterhr/schema"
import { CreditNoteViewer } from "@oysterhr/invoicing"
import { InternalGenerationViewer } from "../components/fileViewer/InternalGenerationViewer"
import { TeamMemberAgreementFragment } from "@oysterhr/hiring/employment-agreements"

export interface FileViewerContextData {
  setViewObject: (obj: SetViewObjectArguments) => void
  loading: boolean
  setLoading: (val: boolean) => void
  error: string | null
  setError: (error: string | null) => void
  closeViewer: () => void
}
export const FileViewerContext = createContext<FileViewerContextData | null>(null)

export const useFileViewer = () => {
  const contextData = useContext(FileViewerContext)
  if (!contextData) {
    throw new Error("useFileViewer: File viewer context must be provided!")
  }

  return contextData
}

export enum ViewableFileType {
  FILE = "file",
  INVOICE = "invoice",
  CREDIT_NOTE = "creditNote",
  DOCUMENT = "document",
  AGREEMENT = "agreement",
  IDG_DOCUMENT = "idgDocument",
}

export type SetViewObjectArguments = {
  file?: Maybe<DownloadLink>
  invoice?: Invoice
  creditNote?: CreditNote
  document?: Document
  idgDocument?: GeneratedDocument
  agreement?: TeamMemberAgreementFragment
  customDrawer?: ReactNode
}

type ActiveObject =
  | {
      type: ViewableFileType.FILE
      object: DownloadLink
    }
  | {
      type: ViewableFileType.INVOICE
      object: Invoice
    }
  | {
      type: ViewableFileType.CREDIT_NOTE
      object: CreditNote
    }
  | {
      type: ViewableFileType.AGREEMENT
      object: TeamMemberAgreementFragment
    }
  | {
      type: ViewableFileType.DOCUMENT
      object: Document
    }
  | {
      type: ViewableFileType.IDG_DOCUMENT
      object: GeneratedDocument
    }

type FileViewerProviderProps = PropsWithChildren<unknown>

export const FileViewerProvider = ({ children }: FileViewerProviderProps) => {
  const [activeObject, setActiveObject] = useState<ActiveObject | null>(null)
  const [customDrawer, setCustomDrawer] = useState<ReactNode>()

  const [isOpen, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<null | string>(null)

  const close = useCallback(() => {
    setOpen(false)
  }, [])

  const setViewObject = useCallback(
    ({
      file: newFile,
      invoice: newInvoice,
      creditNote: newCreditNote,
      document: newDocument,
      idgDocument: newIdgDocument,
      agreement: newAgreement,
      customDrawer,
    }: SetViewObjectArguments) => {
      let newActiveObject = null
      setError(null)
      setLoading(true)
      if (newFile) {
        setActiveObject({
          type: ViewableFileType.FILE,
          object: newFile,
        })
        newActiveObject = newFile
      }
      if (newInvoice) {
        setActiveObject({
          type: ViewableFileType.INVOICE,
          object: newInvoice,
        })
        newActiveObject = newInvoice
      }
      if (newCreditNote) {
        setActiveObject({
          type: ViewableFileType.CREDIT_NOTE,
          object: newCreditNote,
        })
        newActiveObject = newCreditNote
      }
      if (newDocument) {
        setActiveObject({
          type: ViewableFileType.DOCUMENT,
          object: newDocument,
        })

        newActiveObject = newDocument
      }
      if (newAgreement) {
        setActiveObject({
          type: ViewableFileType.AGREEMENT,
          object: newAgreement,
        })
        newActiveObject = newAgreement
      }

      if (newIdgDocument) {
        setActiveObject({
          type: ViewableFileType.IDG_DOCUMENT,
          object: newIdgDocument,
        })
        newActiveObject = newIdgDocument
      }

      setCustomDrawer(customDrawer)

      const untypedObject = newActiveObject as any
      const isImage =
        untypedObject?.file?.contentType?.includes("image") ||
        untypedObject?.ownContractFile?.contentType.includes("image") ||
        (untypedObject?.filename && !untypedObject?.filename.endsWith(".pdf"))
      setOpen(true)
      if (isImage) {
        setLoading(false)
      }
    },
    [],
  )

  let content = null

  if (isOpen) {
    // For small devices open pdf in new tab

    switch (activeObject?.type) {
      case ViewableFileType.FILE:
        content = (
          <GenericFileViewer
            file={activeObject?.object}
            onClose={close}
            customDrawer={customDrawer}
          />
        )
        break
      case ViewableFileType.INVOICE:
        content = <InvoiceViewer invoice={activeObject?.object} close={close} />
        break
      case ViewableFileType.CREDIT_NOTE:
        content = <CreditNoteViewer creditNote={activeObject?.object} close={close} />
        break
      case ViewableFileType.DOCUMENT:
        content = <DocumentViewer document={activeObject?.object} close={close} />
        break
      case ViewableFileType.AGREEMENT:
        content = (
          <AgreementViewer
            agreement={activeObject?.object}
            close={close}
            customDrawer={customDrawer}
          />
        )
        break
      case ViewableFileType.IDG_DOCUMENT:
        content = (
          <InternalGenerationViewer
            document={activeObject?.object}
            close={close}
            customDrawer={customDrawer}
          />
        )
        break
      default:
        content = null
    }
  }

  return (
    <ErrorBoundary>
      <FileViewerContext.Provider
        value={{
          setViewObject,
          loading,
          setLoading,
          error,
          setError,
          closeViewer: close,
        }}
      >
        {content}
        {children}
      </FileViewerContext.Provider>
    </ErrorBoundary>
  )
}
